import React, { FunctionComponent, ReactNode, useMemo } from 'react';
import WithDataRecord from 'hoc/WithDataRecord';
import {
  AdminApi,
  AdminVouchersListRequest,
  AdminVoucher,
  AdminVouchersListOrderingEnum,
  AdminVouchersValuesRetrieveFieldEnum,
} from 'api';
import useGoTo from 'hooks/use-go-to';
import { ISSUER_ROUTES } from 'apps/issuer/pages/routes.config';
import Translate from 'src/ui/atoms/translate';
import CopyButton from 'src/ui/molecules/copy-button';
import DataRecordTable, { CellTypeEnum } from 'src/libraries/data-record-table';
import { PaginationProps } from 'src/ui/molecules/pagination';
import DataRecordOrdering from 'src/ui/types/data-ordering';
import StatusTag from 'src/ui/atoms/status-tag';
import useApiCall from 'src/hooks/use-api-call';
import { useTranslateWithStringArgs } from 'src/ui/hooks/use-translate';
import { Search } from 'src/ui/molecules/filtering';

export interface StudioVouchersTableProps {
  ordering?: DataRecordOrdering;
  onOrderBy?: (fieldName: string) => void;
  vouchers?: AdminVoucher[];
  goToDetails: (id: string) => void;
  title?: ReactNode;
  loading?: boolean;
  paginationProps?: PaginationProps;
  defaultLimit?: number;
  hasPermission: boolean;
  setFilterPopupHeight?: (height: number) => void;
}

const StudioVouchersTable: FunctionComponent<StudioVouchersTableProps> = (props) => {
  const {
    ordering,
    onOrderBy,
    vouchers,
    goToDetails,
    title,
    hasPermission,
    loading,
    paginationProps,
    setFilterPopupHeight,
  } = props;

  const { makeAuthenticatedApi } = useApiCall();
  const translate = useTranslateWithStringArgs();
  const adminApi: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);

  const voucherStatusFilterOptions = useMemo(() => {
    return ['true', 'false'].map((active) => ({
      id: active,
      name: translate(`studioVouchersTable.${active === 'true' ? 'active' : 'deactivated'}`),
    }));
  }, [translate]);

  return (
    <DataRecordTable
      ordering={ordering}
      onOrderBy={onOrderBy}
      data={vouchers}
      title={title}
      loading={loading}
      hasPermission={hasPermission}
      paginationProps={paginationProps}
      onGoToDetails={goToDetails}
      setFilterPopupHeight={setFilterPopupHeight}
      searchComponent={
        <Search input="search" defaultField="search" label={translate('filters.labels.name')} name="search" />
      }
      cells={[
        {
          title: <Translate name={`studioVouchersTable.name`} />,
          render: ({ name }) => name,
          orderingFieldName: AdminVouchersListOrderingEnum.NAME_ASC,
          type: CellTypeEnum.text,
          filterName: 'name',
          searchFieldName: AdminVouchersValuesRetrieveFieldEnum.NAME,
          getFilterData: (params: any) => adminApi.adminVouchersValuesRetrieve(params),
        },
        {
          title: <Translate name={`studioVouchersTable.voucherCode`} />,
          render: ({ code }) => <span>{code} </span>,
          copyValue: ({ code }) => <CopyButton valueToCopy={code} />,
          orderingFieldName: AdminVouchersListOrderingEnum.CODE_ASC,
          type: CellTypeEnum.text,
          filterName: 'code',
          searchFieldName: AdminVouchersValuesRetrieveFieldEnum.CODE,
          getFilterData: (params: any) => adminApi.adminVouchersValuesRetrieve(params),
        },
        {
          title: <Translate name={`studioVouchersTable.alreadyUsed`} />,
          alignContent: 'right',
          render: ({ alreadyUsed }) => alreadyUsed,
          orderingFieldName: AdminVouchersListOrderingEnum.ALREADY_USED_ASC,
          type: CellTypeEnum.number,
          filterName: 'alreadyUsed',
        },
        {
          title: <Translate name={`studioVouchersTable.status`} />,
          render: ({ active }) => (
            <StatusTag variant={active ? 'success' : 'danger'} truncate={true}>
              <Translate name={`studioVouchersTable.${active ? 'active' : 'deactivated'}`} />
            </StatusTag>
          ),
          orderingFieldName: AdminVouchersListOrderingEnum.ACTIVE_ASC,
          width: 135,
          filterName: 'active',
          getFilterData: () => {
            return voucherStatusFilterOptions;
          },
        },
      ]}
    />
  );
};
interface IssuerVouchersTableProps extends AdminVouchersListRequest {
  filters?: ReactNode;
  defaultLimit?: number;
  forceDataLoad?: boolean;
  setForceDataLoad?: (val: boolean) => void;
  hasPermission: boolean;
  setFilterPopupHeight?: (height: number) => void;
}

const IssuerVouchersTable: FunctionComponent<IssuerVouchersTableProps> = WithDataRecord<AdminApi, AdminVoucher>(
  AdminApi,
)(
  ({ data, ordering, onOrderBy, loading, hasPermission, paginationProps, setFilterPopupHeight }) => {
    const goTo = useGoTo(ISSUER_ROUTES.voucherDetails);

    return (
      <>
        <StudioVouchersTable
          loading={loading}
          hasPermission={hasPermission}
          onOrderBy={onOrderBy}
          ordering={ordering}
          goToDetails={(id: string) => goTo({ voucherId: id })}
          vouchers={data}
          paginationProps={paginationProps}
          setFilterPopupHeight={setFilterPopupHeight}
        />
      </>
    );
  },
  (api, props, offset, limit, ordering) => {
    return api.adminVouchersList({
      offset,
      limit,
      ordering: ordering as AdminVouchersListOrderingEnum,
      ...props,
    });
  },
);

export default IssuerVouchersTable;
